import React from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';

// Anchored below the textarea (not the literal caret position) --
// deliberately the simpler, robust option: measuring exact caret pixel
// coordinates inside a plain <textarea> is fragile across browsers/fonts,
// and this same "below the input" anchoring is already this app's own
// established pattern for the global search bar's own SuggestionList.
const MentionSuggestionDropdown = ({ suggestions, highlightedIndex, loading, onSelect }) => {
  const { t } = useTranslation();
  if (!suggestions.length && !loading) return null;

  return (
    <ul
      className="absolute left-0 right-0 top-full mt-1 z-50 max-h-56 overflow-y-auto
                 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl"
      role="listbox"
      aria-label={t('groups.mentionDropdown.aria')}
    >
      {loading && suggestions.length === 0 && (
        <li className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{t('groups.mentionDropdown.searching')}</li>
      )}
      {suggestions.map((user, i) => (
        <li
          key={user.id ?? user.username}
          role="option"
          aria-selected={i === highlightedIndex}
          onMouseDown={(e) => {
            // onMouseDown (not onClick) fires before the textarea's own
            // onBlur, so selecting a suggestion doesn't lose the composer's
            // cursor position/focus first.
            e.preventDefault();
            onSelect(user);
          }}
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${
            i === highlightedIndex ? 'bg-violet-600/30 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-cream-900/5 dark:hover:bg-white/5'
          }`}
        >
          <User className="w-4 h-4 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />
          <span className="font-semibold truncate">@{user.username}</span>
          {user.full_name && (
            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{user.full_name}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

export default MentionSuggestionDropdown;
